import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Badge } from '@/components/atoms/Badge';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Pin } from 'lucide-react';
import { toast } from '@/store/toastStore';

/**
 * 어드민 공지사항 관리 — 운영/공지 게시판(board_id=12) 의 글을 관리.
 * 신규/수정은 모달이 아닌 별도 페이지 (NoticeFormPage) 로 이동.
 */

const NOTICE_OPS_BOARD_ID = 12;

interface AdminPost {
  id: number;
  board_id: number;
  board_name: string | null;
  title: string;
  user_id: string;
  author_nickname: string | null;
  is_blinded: boolean;
  is_deleted: boolean;
  view_count: number;
  comment_count: number;
  created_at: string;
}

function fmt(iso: string) {
  return new Date(iso).toISOString().slice(0, 16).replace('T', ' ');
}

export function NoticesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'notices', 'list'],
    queryFn: () =>
      get<{ posts: AdminPost[]; total: number }>(
        `/admin/posts?board_id=${NOTICE_OPS_BOARD_ID}&limit=50`,
      ),
  });

  /* 핀 OFF / 삭제 액션 — 리스트 inline */
  const refetch = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'notices', 'list'] });
    qc.invalidateQueries({ queryKey: ['posts', 'pinned'] });
  };
  const deleteMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      post(`/admin/posts/${id}/delete`, { reason }),
    onSuccess: () => {
      toast.success('삭제 완료');
      refetch();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? '삭제 실패'),
  });

  return (
    <div className="w-full">
      <div className="mb-6 flex items-center justify-between gap-2">
        <h1 className="text-2xl font-extrabold">공지사항 관리</h1>
        <Button onClick={() => navigate('/admin/notices/new')}>+ 새 공지</Button>
      </div>

      <div className="mb-4 rounded-md border border-[var(--color-border)] bg-[var(--color-info-bg)] p-3 text-xs text-[var(--color-info)]">
        공지는 <strong>운영/공지 게시판(board=12)</strong>에 작성됩니다.
        고정 항목은 <strong>모든 게시판 상단의 공지 배너</strong>에 노출돼요. 최대 5개까지 고정 가능.
      </div>

      {isLoading ? (
        <Skeleton className="h-80" />
      ) : (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
          {data?.posts.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-3 border-t border-[var(--color-border)] p-3 first:border-t-0"
            >
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex items-center gap-1.5">
                  {/* PinnedNoticesBanner 와 동일 시멘틱 — 고정 글이면 Pin 뱃지 */}
                  {/* board_id 체크는 이미 board=12 필터로 통과 */}
                  <Badge variant="outline">{p.board_name ?? '운영/공지'}</Badge>
                  {p.is_deleted && <Badge variant="default">삭제</Badge>}
                  {p.is_blinded && <Badge variant="fall">블라인드</Badge>}
                  <Link
                    to={`/post/${p.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="truncate text-sm font-semibold hover:underline"
                  >
                    {p.title}
                  </Link>
                </div>
                <div className="text-xs text-[var(--color-text-sub)]">
                  {p.author_nickname ?? '-'} · {fmt(p.created_at)} · 조회 {p.view_count}
                  {p.comment_count > 0 && ` · 댓글 ${p.comment_count}`}
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => navigate(`/admin/notices/${p.id}/edit`)}
              >
                수정
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={() => {
                  const reason = prompt('삭제 사유 (2자 이상)');
                  if (reason && reason.trim().length >= 2) {
                    deleteMut.mutate({ id: p.id, reason: reason.trim() });
                  }
                }}
              >
                삭제
              </Button>
            </div>
          ))}
          {data?.posts.length === 0 && (
            <div className="p-8 text-center text-sm text-[var(--color-text-sub)]">
              공지가 없어요. 우측 상단 "+ 새 공지" 로 첫 공지를 등록하세요.
            </div>
          )}
        </div>
      )}

      {/* 고정 글 영역 */}
      <PinnedSection />
    </div>
  );
}

interface PinnedItem {
  id: number;
  title: string;
  category_name: string | null;
  pin_order: number;
  created_at: string;
}

function PinnedSection() {
  const { data, isLoading } = useQuery({
    queryKey: ['posts', 'pinned'],
    queryFn: () => get<PinnedItem[]>('/posts/pinned'),
    staleTime: 60_000,
  });
  const list = data ?? [];

  return (
    <section className="mt-8">
      <h2 className="mb-3 flex items-center gap-2 text-lg font-bold">
        <Pin className="h-4 w-4 text-[var(--color-primary)]" />
        현재 고정된 공지 ({list.length}/5)
      </h2>
      {isLoading ? (
        <Skeleton className="h-24" />
      ) : list.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] p-6 text-center text-sm text-[var(--color-text-sub)]">
          고정된 공지가 없어요.
        </div>
      ) : (
        <ol className="space-y-1.5">
          {list.map((p) => (
            <li
              key={p.id}
              className="flex items-center gap-2 rounded-lg border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5 px-3 py-2 text-sm"
            >
              <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-primary)] text-xs font-bold text-[var(--color-primary-inv)]">
                {p.pin_order || '?'}
              </span>
              <Link
                to={`/post/${p.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="truncate font-medium hover:underline"
              >
                {p.title}
              </Link>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
